import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  Typography,
  Chip,
} from "@mui/material";
import { Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";

import ListTable from "../../common/Table";
import GroupModal from "./GroupModal";
import OrganisatioFilter from "../Statisctics/OrganisationFilter";

import { roles } from "../../../constants/routes";
import { getRole } from "../../../helpers/auth";
import { useOrganisation } from "../../../hooks/useOrganisation";
import Button from "../../common/Button";
import {
  addGroup,
  deleteGroup,
  deleteGroups,
  editGroup,
  getAllGroups,
} from "../../../store/api/groups";
import { groupColumns } from "./columnsGroup";
import { getAllUsersByAdmin } from "../../../store/api/users";
import CommonSearch from "../../common/Search";
import { columns } from "./columnsUser";

const ManageGroups = () => {
  const queryClient = useQueryClient();
  const role = getRole();
  const { primaryColor } = useOrganisation();

  // Groups state
  const [searchTerm, setSearchTerm] = useState("");
  const [allGroups, setAllGroups] = useState([]);
  const [groupsTotalPage, setGroupsTotalPage] = useState(1);
  const [groupsCurrentPage, setGroupsCurrentPage] = useState(1);
  const [groupsSortBy, setGroupsSortBy] = useState("name");
  const [groupsIsDescending, setGroupsIsDescending] = useState(false);

  // Users in group state
  const [usersSearchTerm, setUsersSearchTerm] = useState("");
  const [usersTotalPage, setUsersTotalPage] = useState(1);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersSortBy, setUsersSortBy] = useState("fullName");
  const [usersIsDescending, setUsersIsDescending] = useState(false);

  // Common state
  const [selectedIds, setSelectedIds] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [organisation, setOrganisation] = useState(null);

  // Single group deletion
  const [singleGroupToDelete, setSingleGroupToDelete] = useState(null);
  const [openSingleDelete, setOpenSingleDelete] = useState(false);

  // Group users view
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [showingGroupUsers, setShowingGroupUsers] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Query for groups
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: [
      "adminGroups",
      searchTerm,
      groupsCurrentPage,
      groupsSortBy,
      groupsIsDescending,
      organisation,
    ],
    queryFn: () =>
      getAllGroups({
        searchTerm,
        pageNumber: groupsCurrentPage,
        sortBy: groupsSortBy,
        isDescending: groupsIsDescending,
        organisationId: organisation?.id,
      }),
    enabled: !showingGroupUsers,
  });

  // Query for viewing users in a group
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: [
      "groupUsers",
      selectedGroupId,
      usersCurrentPage,
      usersSortBy,
      usersIsDescending,
      usersSearchTerm,
      organisation,
    ],
    queryFn: () =>
      getAllUsersByAdmin({
        groupId: selectedGroupId,
        searchTerm: usersSearchTerm,
        currentPage: usersCurrentPage,
        sortBy: usersSortBy,
        isDescending: usersIsDescending,
        organisationId: organisation?.id,
      }),
    enabled: !!selectedGroupId && showingGroupUsers,
  });

  const { mutate: addMutate } = useMutation({
    mutationFn: (group) => addGroup(group),
    onSuccess: () => {
      queryClient.refetchQueries(["adminGroups"]);
      setToast({
        open: true,
        message: "Group added successfully",
        severity: "success",
      });
      setIsAddModalOpen(false);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.title ||
        "An error occurred while adding the group";
      setToast({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    },
  });

  const { mutate: editMutate } = useMutation({
    mutationFn: (group) => editGroup(group),
    onSuccess: () => {
      queryClient.refetchQueries(["adminGroups"]);
      setToast({
        open: true,
        message: "Group updated successfully",
        severity: "success",
      });
      setIsEditMode(false);
      setGroupToEdit(null);
      setIsAddModalOpen(false);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.title ||
        "An error occurred while editing the group";
      setToast({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    },
  });

  const { mutate: deleteMutate } = useMutation({
    mutationFn: (ids) => deleteGroups(ids),
    onSuccess: () => {
      setToast({
        open: true,
        message: "Groups deleted successfully",
        severity: "success",
      });
      queryClient.refetchQueries(["adminGroups"]);
      setSelectedIds([]);
    },
    onError: (error) => {
      setToast({
        open: true,
        message: "Error deleting groups",
        severity: "error",
      });
    },
  });

  const { mutate: deleteSingleMutate } = useMutation({
    mutationFn: (id) => deleteGroup(id),
    onSuccess: () => {
      setToast({
        open: true,
        message: "Group deleted successfully",
        severity: "success",
      });
      queryClient.refetchQueries(["adminGroups"]);
      setSingleGroupToDelete(null);
    },
    onError: (error) => {
      setToast({
        open: true,
        message: "Error deleting group",
        severity: "error",
      });
    },
  });

  useEffect(() => {
    if (!isLoadingGroups && groupsData) {
      setAllGroups(groupsData.paginatedContent);
      setGroupsTotalPage(groupsData.totalPages);
    }
  }, [isLoadingGroups, groupsData]);

  useEffect(() => {
    if (!isLoadingUsers && usersData) {
      setUsersTotalPage(usersData.totalPages);
    }
  }, [isLoadingUsers, usersData]);

  // Reset user pagination when switching groups
  useEffect(() => {
    if (selectedGroupId) {
      setUsersCurrentPage(1);
    }
  }, [selectedGroupId]);

  const handleOpenModal = () => {
    setIsEditMode(false);
    setGroupToEdit(null);
    setIsAddModalOpen(true);
  };

  const handleEditGroup = (id) => {
    const groupData = allGroups.find((group) => group.id === id);
    setGroupToEdit(groupData);
    setIsEditMode(true);
    setIsAddModalOpen(true);
  };

  // Handler for viewing users in a group - redirects to user tab
  const handleViewGroupUsers = (groupId, groupName) => {
    if (groupId && groupName) {
      // Use a custom event to communicate with parent component
      const redirectEvent = new CustomEvent("redirectToUserTab", {
        detail: { groupId, groupName },
      });
      window.dispatchEvent(redirectEvent);
    } else {
      console.error("Invalid group data:", { groupId, groupName });
    }
  };

  // Handler for clearing the group filter
  const handleClearGroupFilter = () => {
    setSelectedGroupId(null);
    setSelectedGroupName("");
    setShowingGroupUsers(false);
    setUsersSearchTerm("");
  };

  const handleDeleteGroup = (id) => {
    setSingleGroupToDelete(id);
    setOpenSingleDelete(true);
  };

  const handleDeleteSingleGroup = () => {
    if (singleGroupToDelete) {
      deleteSingleMutate(singleGroupToDelete);
    }
    setOpenSingleDelete(false);
  };

  const handleCloseSingleDelete = () => {
    setOpenSingleDelete(false);
    setSingleGroupToDelete(null);
  };

  const handleSubmitGroup = (group) => {
    if (isEditMode && groupToEdit) {
      editMutate({ name: group, id: groupToEdit.id });
    } else {
      addMutate(group);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === allGroups.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allGroups.map((group) => group.id));
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId) => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };

  const handleDelete = () => {
    deleteMutate(selectedIds);
    setOpenDelete(false);
  };

  const handleOpenDelete = () => {
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  return (
    <div>
      <Box>
        {showingGroupUsers && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1">Showing users in group:</Typography>
            <Chip
              label={selectedGroupName}
              color="primary"
              onDelete={handleClearGroupFilter}
              deleteIcon={<CloseIcon />}
            />
          </Box>
        )}

        <Grid container flex={1} justifyContent={"space-between"}>
          <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
            <CommonSearch
              placeholder={showingGroupUsers ? "Search users" : "Search groups"}
              onSearch={showingGroupUsers ? setUsersSearchTerm : setSearchTerm}
              initialValue={showingGroupUsers ? usersSearchTerm : searchTerm}
              sx={{ flexGrow: 1, marginRight: 2 }}
            />
            {!showingGroupUsers && (
              <Button
                endIcon={<ControlPointIcon />}
                onClick={handleOpenModal}
                text="Add group"
              />
            )}
          </Box>
          {!showingGroupUsers && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <Button
                endIcon={<DeleteIcon />}
                onClick={handleOpenDelete}
                disabled={selectedIds.length === 0}
                text="Delete group"
                backgroundColor="white"
              />
            </Box>
          )}
          {roles.SuperAdmin === role && (
            <OrganisatioFilter setOrganisation={setOrganisation} />
          )}
        </Grid>
      </Box>

      {showingGroupUsers ? (
        <ListTable
          data={usersData?.paginatedContent || []}
          columns={columns({
            setSortBy: setUsersSortBy,
            setIsDescending: setUsersIsDescending,
            isDescending: usersIsDescending,
            handleEditUser: () => {}, // Empty function as this is view-only mode
            handleSelectAll: () => {}, // Empty function as this is view-only mode
            selectedIds: [],
            handleSelect: () => {}, // Empty function as this is view-only mode
          })}
          isLoading={isLoadingUsers}
          totalPage={usersTotalPage || 1}
          setCurrentPage={setUsersCurrentPage}
          currentPage={usersCurrentPage}
        />
      ) : (
        <ListTable
          data={allGroups}
          columns={groupColumns({
            setSortBy: setGroupsSortBy,
            setIsDescending: setGroupsIsDescending,
            isDescending: groupsIsDescending,
            handleEditGroup,
            handleDeleteGroup,
            handleSelectAll,
            selectedIds,
            handleSelect,
            handleViewGroupUsers,
          })}
          isLoading={isLoadingGroups}
          totalPage={groupsTotalPage}
          setCurrentPage={setGroupsCurrentPage}
          currentPage={groupsCurrentPage}
        />
      )}

      <GroupModal
        open={isAddModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitGroup}
        isEdit={isEditMode}
        group={groupToEdit}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure you want to delete the selected groups?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Deleting these groups cannot be undone. Please confirm if you want
            to proceed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDelete}
            text="Cancel"
            backgroundColor="white"
          />
          <Button onClick={handleDelete} text="Delete" />
        </DialogActions>
      </Dialog>

      {/* Single Delete Dialog */}
      <Dialog
        open={openSingleDelete}
        onClose={handleCloseSingleDelete}
        aria-labelledby="single-delete-dialog-title"
        aria-describedby="single-delete-dialog-description"
      >
        <DialogTitle id="single-delete-dialog-title">
          {"Delete Group"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="single-delete-dialog-description">
            Are you sure you want to delete this group? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSingleDelete}
            text="Cancel"
            backgroundColor="white"
          />
          <Button onClick={handleDeleteSingleGroup} text="Delete" />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ManageGroups;
